import { Tile } from '@/engine/Tile';
import { pool } from '@/engine/pool';
import { bestFitGrid } from '@/layout/bestFitGrid';
import { SLOT_COUNT } from '@/config';
import { state, save, wallCams, viewCams, viewFiltered, normalizeForLayout, type ViewFilter } from '@/store/state';
import { cctvSupported } from '@/config/cctv';
import { resetReviver } from '@/engine/revive';
import { $, must, flip } from '@/lib/dom';
import { toast } from './toast';
import * as bus from '@/lib/bus';
import { attachDragSource, type DragOrigin } from './drag';
import type { Layout } from '@/engine/types';

const tiles = new Map<string, Tile>();
let gridEl: HTMLElement;
let stageEl: HTMLElement;
let io: IntersectionObserver;
let kioskFit = false;

/**
 * Effective layout — kiosk and any active view filter force 'fit' regardless of
 * the persisted layout (a filtered wall has no stable slot positions to keep).
 */
const effLayout = (): Layout => (kioskFit || viewFiltered() ? 'fit' : state.layout);

/** Change the view filter, then re-render everything that depends on it. */
export function setView(patch: Partial<ViewFilter>): void {
  const wasFiltered = viewFiltered();
  state.view = { ...state.view, ...patch };
  if (!cctvSupported() && state.view.source === 'cctv') state.view.source = 'all';
  // leaving a filtered (forced-fit) view: re-pad the fixed layout's slots
  if (wasFiltered && !viewFiltered() && !kioskFit) normalizeForLayout(state.layout);
  if (patch.onlineOnly) resetReviver();
  save();
  pool.refreshMax();
  renderWall();
  bus.emit('view:changed', undefined);
}

/** Kiosk fullscreen forces a fit view; exiting restores + re-pads the fixed layout. */
export function setKioskFit(on: boolean): void {
  kioskFit = on;
  if (!on) normalizeForLayout(state.layout);
  renderWall();
}

export function reloadAll(): void {
  tiles.forEach((t) => { if (t.attached) t.hardReinit(); });
}

export function initGrid(): void {
  gridEl = must('#grid');
  stageEl = must('#stage');
  io = new IntersectionObserver((entries) => {
    for (const e of entries) {
      const t = tiles.get((e.target as HTMLElement).dataset['id'] ?? '');
      if (!t) continue;
      t.visible = e.isIntersecting && e.intersectionRatio > 0.05;
      if (t.visible) t.lastVisible = Date.now();
    }
    pool.reconcile();
  }, { root: stageEl, rootMargin: '250px', threshold: [0, 0.06] });
  new ResizeObserver(() => applyFit()).observe(stageEl);

  // "Online only": a tile that just went offline should leave the wall, and a
  // camera on the wall with no tile yet (it was hidden) should come back if it
  // is seen live again. Debounced, since statuses arrive in bursts.
  let rt: number | undefined;
  bus.on('cam:status', (id) => {
    if (!state.view.onlineOnly) return;
    const c = state.cameras[id];
    if (!c || !state.onWall.includes(id)) return;
    const hidden = c.status === 'offline' || c.status === 'absent';
    if (hidden === !tiles.has(id)) return; // already rendered the right way
    clearTimeout(rt);
    rt = window.setTimeout(renderWall, 300);
  });
}

function ensureTile(id: string): Tile {
  const existing = tiles.get(id);
  if (existing) return existing;
  const t = new Tile(state.cameras[id]!);
  tiles.set(id, t);
  pool.register(t);
  io.observe(t.el);
  attachDragSource(t.el, () => ({ kind: 'tile', cam: id, fromSlot: Number(t.el.dataset['slot'] ?? -1) }));
  return t;
}

function destroyTile(id: string): void {
  const t = tiles.get(id);
  if (!t) return;
  io.unobserve(t.el);
  pool.unregister(t);
  t.detach();
  t.el.classList.add('leaving');
  t.el.addEventListener('transitionend', () => t.el.remove(), { once: true });
  tiles.delete(id);
}

function emptySlot(i: number): HTMLElement {
  const el = document.createElement('div');
  el.className = 'slot slot--empty';
  el.dataset['slot'] = String(i);
  el.innerHTML = `<div class="slot__hint"><span class="slot__plus">+</span><span class="slot__label">ลากกล้องมาวาง</span></div>`;
  return el;
}

function appendSlot(): HTMLElement {
  const el = document.createElement('div');
  el.className = 'slot slot--append';
  el.dataset['slot'] = 'append';
  el.innerHTML = `<div class="slot__hint"><span class="slot__plus">+</span></div>`;
  return el;
}

export function renderWall(): void {
  const layout = effLayout();
  gridEl.dataset['l'] = layout;
  const shown = viewCams();
  const wanted = new Set(shown);
  // drop tiles no longer on the wall
  for (const id of [...tiles.keys()]) if (!wanted.has(id)) destroyTile(id);

  flip(gridEl, '.tile', () => {
    // rebuild slot order without disturbing existing tile elements (they carry live video)
    const kept = new Set<HTMLElement>();
    const place = (node: HTMLElement, i: number): void => {
      node.dataset['slot'] = String(i);
      gridEl.appendChild(node);
      kept.add(node);
    };
    if (layout === 'fit') {
      shown.forEach((id, i) => place(ensureTile(id).el, i));
      const app = appendSlot();
      gridEl.appendChild(app);
      kept.add(app);
    } else {
      const n = SLOT_COUNT[layout];
      for (let i = 0; i < n; i++) {
        const id = state.onWall[i];
        place(id ? ensureTile(id).el : emptySlot(i), i);
      }
    }
    // remove any stragglers (old empty slots)
    for (const child of [...gridEl.children]) if (!kept.has(child as HTMLElement)) child.remove();
  });

  const empty = $('#empty') as HTMLElement;
  empty.classList.toggle('hide', wanted.size > 0);
  if (!wanted.size) {
    const filteredOut = viewFiltered() && wallCams().length > 0;
    $('.et', empty)!.textContent = filteredOut ? 'ไม่มีกล้องที่ตรงตัวกรอง' : 'ยังไม่มีกล้องบนวอลล์';
    $('.es', empty)!.textContent = filteredOut ? 'ลองเปลี่ยนแหล่งกล้อง หรือปิด "เฉพาะออนไลน์"' : 'เลือกกล้องจากแผงด้านซ้าย';
  }
  applyFit();
  pool.reconcile();
  refreshQualityLabels();
}

export function applyFit(): void {
  if (gridEl?.dataset['l'] !== 'fit') { gridEl?.style.removeProperty('--tw'); gridEl?.style.removeProperty('--th'); return; }
  requestAnimationFrame(() => {
    const n = viewCams().length;
    if (!n) return;
    const cs = getComputedStyle(gridEl);
    const pad = parseFloat(cs.paddingLeft) || 0;
    const gap = state.gutter === 'comfy' ? 8 : 6;
    const W = gridEl.clientWidth - 2 * pad;
    const H = gridEl.clientHeight - 2 * pad;
    const f = bestFitGrid(n, W, H, gap);
    gridEl.style.setProperty('--tw', `${f.tileW}px`);
    gridEl.style.setProperty('--th', `${f.tileH}px`);
  });
}

export const isOnWall = (id: string): boolean => state.onWall.includes(id);

/** Add/remove a camera. Fixed layouts fill the first empty slot; 'fit' appends. */
export function setWall(id: string, on: boolean): void {
  const has = isOnWall(id);
  const fit = effLayout() === 'fit';
  if (on && !has) {
    if (fit) state.onWall.push(id);
    else {
      const hole = state.onWall.indexOf(null);
      if (hole === -1) { toast('วอลล์เต็ม — เปลี่ยน layout หรือเอากล้องออกก่อน', 'warn'); return; }
      state.onWall[hole] = id;
    }
  } else if (!on && has) {
    if (fit) state.onWall = state.onWall.filter((x) => x !== id);
    else state.onWall = state.onWall.map((x) => (x === id ? null : x));
  } else return;
  commit();
}

export function removeFromWall(id: string): void { setWall(id, false); }

export function forcePlay(id: string): void { tiles.get(id)?.forcePlay(); }

export function refreshQualityLabels(): void { tiles.forEach((t) => t.updateQualityLabel()); }

export function applyGlobalQuality(): void {
  tiles.forEach((t) => {
    if (!state.quality[t.cam.id]) t.applyQuality(state.globalQuality);
    t.updateQualityLabel();
  });
}

export function setLayout(layout: Layout): void {
  state.layout = layout;
  const dropped = normalizeForLayout(layout);
  if (dropped.length) toast(`ย้ายไม่ครบ ${dropped.length} กล้อง (วอลล์เต็ม)`, 'warn');
  commit();
}

function commit(): void {
  save();
  renderWall();
  bus.emit('wall:changed', undefined);
}

/** Resolve a drop onto a target slot/tile element. Mutates onWall + persists. */
export function gridApplyDrop(origin: DragOrigin, target: HTMLElement): void {
  if (effLayout() === 'fit') applyDropFit(origin, target);
  else applyDropFixed(origin, target);
  commit();
}

function applyDropFixed(origin: DragOrigin, target: HTMLElement): void {
  const w = state.onWall;
  const to = Number(target.dataset['slot']);
  if (Number.isNaN(to)) return;
  const occupant = w[to] ?? null;
  if (origin.kind === 'tile') {
    const from = origin.fromSlot;
    if (from === to) return;
    w[to] = origin.cam;
    w[from] = occupant; // swap (occupant may be null -> move)
  } else {
    const existing = w.indexOf(origin.cam);
    if (existing !== -1 && existing !== to) w[existing] = null; // moved from elsewhere
    w[to] = origin.cam; // empty -> place, occupied -> replace
  }
}

function applyDropFit(origin: DragOrigin, target: HTMLElement): void {
  // Slot numbers index what is rendered (the filtered view), not the wall, so
  // resolve the drop target to a camera id and insert relative to that.
  const vis = viewCams();
  const w = wallCams();
  const from = w.indexOf(origin.cam);
  if (from !== -1) w.splice(from, 1);
  let insertAt = w.length;
  const append = target.dataset['slot'] === 'append' || target.classList.contains('slot--append');
  const ti = Number(target.dataset['slot']);
  if (!append && !Number.isNaN(ti)) {
    const targetId = vis[ti];
    const at = targetId ? w.indexOf(targetId) : -1;
    if (at !== -1) insertAt = target.classList.contains('insert-right') ? at + 1 : at;
    else if (targetId === origin.cam && from !== -1) insertAt = from; // dropped on itself
  }
  w.splice(Math.max(0, Math.min(w.length, insertAt)), 0, origin.cam);
  state.onWall = w;
}
